import { useRef, useCallback } from "react"

const ANIMATION_MS = 400

export const PinchToZoomImage = (props: { children?: any, onZoomChange?: (zoomed: boolean) => void }) => {
    const { children, onZoomChange } = props
    const containerRef: any = useRef(null)
    const scaleRef: any = useRef(1)
    const translateRef: any = useRef({ x: 0, y: 0 })

    // Pinch gesture state — captured once at pinch start, reused on every move
    const pinchStartDistRef: any = useRef(0)
    const pinchStartScaleRef: any = useRef(1)
    const pinchContentPtRef: any = useRef({ x: 0, y: 0 })  // local content point under initial midpoint
    const containerOriginRef: any = useRef({ x: 0, y: 0 })  // container's untransformed top-left

    // Pan gesture state
    const panStartTouchRef: any = useRef({ x: 0, y: 0 })
    const panStartTranslateRef: any = useRef({ x: 0, y: 0 })

    const doubleTapFiredRef: any = useRef(false)
    const lastTapRef: any = useRef(0)

    const dist = (t1: any, t2: any) =>
        Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)

    const mid = (t1: any, t2: any) => ({
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2,
    })

    // With transform-origin:0 0  and  translate(tx,ty) scale(s):
    //   content pixel (lx,ly) → screen (originX + tx + lx*s, originY + ty + ly*s)
    //   rect.left = originX + tx ,  rect.width = originalWidth * s

    const getOriginalSize = () => {
        const el = containerRef.current
        if (!el) return { w: 1, h: 1 }
        const r = el.getBoundingClientRect()
        return { w: r.width / scaleRef.current, h: r.height / scaleRef.current }
    }

    const clampTranslate = (tx: number, ty: number, s: number) => {
        if (s <= 1) return { x: 0, y: 0 }
        const { w, h } = getOriginalSize()
        // content spans [tx .. tx+w*s], viewport is [0..w]
        // need: tx <= 0  and  tx >= w*(1-s)
        return {
            x: Math.min(0, Math.max(w * (1 - s), tx)),
            y: Math.min(0, Math.max(h * (1 - s), ty)),
        }
    }

    const applyTransform = (animated?: boolean) => {
        const el = containerRef.current
        if (!el) return
        el.style.transition = animated
            ? `transform ${ANIMATION_MS}ms cubic-bezier(0.25, 0.1, 0.25, 1)`
            : "none"
        const s = scaleRef.current
        const { x, y } = translateRef.current
        el.style.transform = `translate(${x}px, ${y}px) scale(${s})`
    }

    const onTouchStart = (e: any) => {
        if (doubleTapFiredRef.current) return

        const el = containerRef.current
        if (!el) return
        el.style.transition = "none"

        if (e.touches.length === 2) {
            e.stopPropagation()
            const r = el.getBoundingClientRect()
            const m = mid(e.touches[0], e.touches[1])

            pinchStartDistRef.current = dist(e.touches[0], e.touches[1])
            pinchStartScaleRef.current = scaleRef.current

            // Container's untransformed top-left (constant during gesture)
            containerOriginRef.current = {
                x: r.left - translateRef.current.x,
                y: r.top - translateRef.current.y,
            }

            // Content-local coords of the point under the midpoint
            pinchContentPtRef.current = {
                x: (m.x - r.left) / scaleRef.current,
                y: (m.y - r.top) / scaleRef.current,
            }
        } else if (e.touches.length === 1 && scaleRef.current > 1) {
            e.stopPropagation()
            panStartTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
            panStartTranslateRef.current = { ...translateRef.current }
        }
    }

    const onTouchMove = (e: any) => {
        if (e.touches.length === 2) {
            e.stopPropagation()
            e.preventDefault()

            const d = dist(e.touches[0], e.touches[1])
            const m = mid(e.touches[0], e.touches[1])
            const newScale = Math.min(4, Math.max(1, pinchStartScaleRef.current * (d / pinchStartDistRef.current)))

            // Solve for translate so that the same content point stays under the current midpoint:
            //   midX = originX + tx + contentPt.x * newScale
            //   tx   = midX - originX - contentPt.x * newScale
            const o = containerOriginRef.current
            const cp = pinchContentPtRef.current
            const tx = m.x - o.x - cp.x * newScale
            const ty = m.y - o.y - cp.y * newScale

            scaleRef.current = newScale
            translateRef.current = newScale > 1 ? { x: tx, y: ty } : { x: 0, y: 0 }
            if (onZoomChange) onZoomChange(newScale > 1)
            applyTransform()
        } else if (e.touches.length === 1 && scaleRef.current > 1) {
            e.stopPropagation()
            const dx = e.touches[0].clientX - panStartTouchRef.current.x
            const dy = e.touches[0].clientY - panStartTouchRef.current.y
            translateRef.current = clampTranslate(
                panStartTranslateRef.current.x + dx,
                panStartTranslateRef.current.y + dy,
                scaleRef.current
            )
            applyTransform()
        }
    }

    const onTouchEnd = (e: any) => {
        // Don't interfere with an in-progress double-tap animation
        if (doubleTapFiredRef.current) return

        if (e.touches.length === 0) {
            if (scaleRef.current <= 1) {
                scaleRef.current = 1
                translateRef.current = { x: 0, y: 0 }
                if (onZoomChange) onZoomChange(false)
            } else {
                // Clamp position so image doesn't stay out of bounds
                translateRef.current = clampTranslate(
                    translateRef.current.x,
                    translateRef.current.y,
                    scaleRef.current
                )
            }
            applyTransform()
        } else if (e.touches.length === 1 && scaleRef.current > 1) {
            // One finger lifted during pinch → switch to pan seamlessly
            panStartTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
            panStartTranslateRef.current = { ...translateRef.current }
        }
    }

    const reset = useCallback(() => {
        scaleRef.current = 1
        translateRef.current = { x: 0, y: 0 }
        if (onZoomChange) onZoomChange(false)
        applyTransform(true)
    }, [])

    const onDoubleTap = (e: any) => {
        if (e.touches.length !== 1) return
        const now = Date.now()
        if (now - lastTapRef.current < 300) {
            e.stopPropagation()
            doubleTapFiredRef.current = true
            setTimeout(() => { doubleTapFiredRef.current = false }, ANIMATION_MS + 50)
            if (scaleRef.current > 1) {
                reset()
            } else {
                const el = containerRef.current
                if (el) {
                    const r = el.getBoundingClientRect()
                    // Local content coords of the tap (scale is 1, tx/ty are 0)
                    const lx = e.touches[0].clientX - r.left
                    const ly = e.touches[0].clientY - r.top
                    const newScale = 2.5
                    // Keep the tap point at the same screen position after zoom
                    const tx = lx * (1 - newScale)
                    const ty = ly * (1 - newScale)
                    scaleRef.current = newScale
                    translateRef.current = clampTranslate(tx, ty, newScale)
                    if (onZoomChange) onZoomChange(true)
                    applyTransform(true)
                }
            }
        }
        lastTapRef.current = now
    }

    return (
        <div
            ref={containerRef}
            onTouchEnd={onTouchEnd}
            onTouchMove={onTouchMove}
            onTouchStart={(e: any) => { onDoubleTap(e); onTouchStart(e) }}
            style={{ transformOrigin: "0 0", willChange: "transform", touchAction: "none" }}
        >
            {children}
        </div>
    )
}
